// ============================================================================
// GradeYourSystem — "Grade your own system" browser probe (PRD §1.9-B, D5).
// ============================================================================
// Reads the browser's own hardware signals (WebGL renderer, deviceMemory,
// hardwareConcurrency), grades the PC into the SAME tier the device-side
// hardware.py would, and shows the expected per-frame latency. Zero abuse
// surface: nothing is uploaded, nothing is sent anywhere — it all runs in the
// prospect's tab. The exact grade comes from the optional downloadable
// grader.exe (affordance + copy only — NOT built here; it reuses hardware.py).
// ============================================================================

import { useEffect, useState } from "react";
import {
  gradeBrowser,
  probeBrowser,
  type BrowserProfile,
  type GradeResult,
} from "./hardwareGrade";
import { TIER_LATENCY, fmtMs } from "./latency";

export function GradeYourSystem() {
  const [profile, setProfile] = useState<BrowserProfile | null>(null);
  const [grade, setGrade] = useState<GradeResult | null>(null);

  useEffect(() => {
    // Probe once on mount (client-only; never throws).
    const p = probeBrowser();
    setProfile(p);
    setGrade(gradeBrowser(p));
  }, []);

  const tierLatency = grade
    ? TIER_LATENCY.find((t) => t.tier === grade.tier)
    : undefined;
  // The per-FLAGGED-frame describer cost is the number a prospect feels.
  const vlmLeg = tierLatency?.legs.find((l) => l.key === "vlm");
  const gateLeg = tierLatency?.legs.find((l) => l.key === "gate");

  return (
    <div className="grade-grid">
      <section className="panel">
        <h3 style={{ marginBottom: 6 }}>What your browser can see</h3>
        <p className="muted" style={{ fontSize: "0.9rem", marginTop: 0 }}>
          Read locally from this tab. Nothing here is uploaded or stored.
        </p>
        <ProbeRow k="Graphics" v={profile?.gpuRenderer ?? "unavailable"} />
        <ProbeRow
          k="GPU type"
          v={
            profile
              ? profile.gpuKind === "dgpu"
                ? "Dedicated GPU"
                : profile.gpuKind === "igpu"
                  ? "Integrated graphics"
                  : "None detected"
              : "—"
          }
        />
        <ProbeRow
          k="Memory"
          v={
            profile?.deviceMemoryGb != null
              ? `${profile.deviceMemoryGb} GB${profile.deviceMemoryGb >= 8 ? "+" : ""}`
              : "not exposed by this browser"
          }
        />
        <ProbeRow
          k="CPU threads"
          v={profile?.logicalCores != null ? String(profile.logicalCores) : "—"}
        />
      </section>

      <section className="panel">
        <h3 style={{ marginBottom: 14 }}>Your estimated tier</h3>
        {grade ? (
          <>
            <span className={"tier-badge " + grade.tier}>
              {grade.tier === "lite"
                ? "Lite"
                : grade.tier === "standard"
                  ? "Standard"
                  : "Max"}
            </span>
            <p className="tier-reason">{grade.reason}</p>

            {tierLatency && gateLeg && vlmLeg && (
              <p className="tier-latency">
                Expected speed on a tier like yours:{" "}
                <b>{fmtMs(gateLeg.ms)}</b> to scan each frame, about{" "}
                <b>{fmtMs(vlmLeg.ms)}</b> to fully describe a flagged one.
              </p>
            )}

            {grade.lowConfidence && (
              <div className="callout" style={{ marginTop: 18 }}>
                This is a browser estimate — some details (exact GPU memory,
                CPU instruction set) aren&apos;t visible to a web page. For an
                exact grade, run the tiny grader below.
              </div>
            )}

            <div style={{ marginTop: 18 }}>
              <button
                className="btn btn-ghost btn-sm"
                type="button"
                onClick={() =>
                  alert(
                    "The signed grader.exe is coming with the beta installer. " +
                      "It reuses the same on-device profiler the app uses, so it " +
                      "gives you the exact tier and timing before you install anything.",
                  )
                }
              >
                Download the exact grader (coming with beta)
              </button>
              <p
                className="mono muted"
                style={{ marginTop: 10, fontSize: "0.72rem" }}
              >
                Optional · ~1 MB · no data leaves your PC
              </p>
            </div>
          </>
        ) : (
          <p className="muted">Probing your system…</p>
        )}
      </section>
    </div>
  );
}

function ProbeRow({ k, v }: { k: string; v: string }) {
  return (
    <div className="probe-row">
      <span className="k">{k}</span>
      <span className="v">{v}</span>
    </div>
  );
}
